import cn from "classnames";
import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";

import styles from "./AdaptiveTable.module.scss";

export interface AdaptiveTableProps
  extends Omit<TableHTMLAttributes<HTMLTableElement>, "children"> {
  children?: ReactNode;
}

export interface AdaptiveTableHeadProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

export interface AdaptiveTableBodyProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children?: ReactNode;
}

export interface AdaptiveTableRowProps
  extends HTMLAttributes<HTMLTableRowElement> {
  children?: ReactNode;
  colspan?: number;
}

export interface AdaptiveTableHeaderCellProps
  extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

export interface AdaptiveTableCellProps
  extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  label?: ReactNode;
}

export interface AdaptiveTableComponent {
  (props: AdaptiveTableProps): JSX.Element;
  Head: (props: AdaptiveTableHeadProps) => JSX.Element;
  Body: (props: AdaptiveTableBodyProps) => JSX.Element;
  Row: (props: AdaptiveTableRowProps) => JSX.Element;
  HeaderCell: (props: AdaptiveTableHeaderCellProps) => JSX.Element;
  Cell: (props: AdaptiveTableCellProps) => JSX.Element;
}

function Head({ children, className, ...props }: AdaptiveTableHeadProps): JSX.Element {
  return (
    <thead className={className} {...props}>
      {children}
    </thead>
  );
}

function Body({ children, className, ...props }: AdaptiveTableBodyProps): JSX.Element {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

function Row({ children, className, colspan, ...props }: AdaptiveTableRowProps): JSX.Element {
  return (
    <tr className={className} {...props}>
      {colspan ? (
        <td colSpan={colspan}>
          {children}
        </td>
      ) : children}
    </tr>
  );
}

function HeaderCell({
  children,
  className,
  scope = "col",
  ...props
}: AdaptiveTableHeaderCellProps): JSX.Element {
  return (
    <th className={className} scope={scope} {...props}>
      {children}
    </th>
  );
}

function Cell({
  children,
  className,
  label,
  ...props
}: AdaptiveTableCellProps): JSX.Element {
  const dataLabel = typeof label === "string" ? label : undefined;
  const hasLabel = label !== undefined && label !== null;

  return (
    <td className={className} data-label={dataLabel} {...props}>
      {hasLabel ? (
        <span className={styles.mobileLabel}>
          {label}
        </span>
      ) : null}
      {children}
    </td>
  );
}

const AdaptiveTable = (({
  children,
  className,
  ...props
}: AdaptiveTableProps): JSX.Element => {
  return (
    <div className={cn(styles.wrapper, className)}>
      <table className={styles.table} {...props}>
        {children}
      </table>
    </div>
  );
}) as AdaptiveTableComponent;

AdaptiveTable.Head = Head;
AdaptiveTable.Body = Body;
AdaptiveTable.Row = Row;
AdaptiveTable.HeaderCell = HeaderCell;
AdaptiveTable.Cell = Cell;

export default AdaptiveTable;
